import type { Noop } from "react-hook-form";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { MarkdownContent } from "../common/markdown-content";
import { Checkbox } from "./";
import type { Option } from "./form.types";

const checkboxGroupVariants = tv({
  base: "flex",
  variants: {
    orientation: {
      vertical: "flex-col gap-2.5",
      horizontal: "items-center gap-6",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

interface CheckboxGroupProps
  extends VariantProps<typeof checkboxGroupVariants> {
  id: string | undefined;
  "aria-invalid": boolean;
  onChange: (value: string[]) => void;
  onBlur: Noop;
  disabled?: boolean;
  name: string;
  className?: string;
  value: string[];
  options: readonly Option<string>[];
}

export const CheckboxGroup = ({
  className,
  disabled,
  id,
  options,
  orientation,
  value = [],
  onChange,
  ...rest
}: CheckboxGroupProps) => {
  return (
    <div
      role="group"
      id={id}
      className={checkboxGroupVariants({ orientation, className })}
      {...rest}
    >
      {options.map((option) => (
        <Checkbox.Item
          key={`${id}-${option.value}-${option.label}`}
          value={option.value}
          checked={value.includes(option.value)}
          onCheckedChange={(checked) => {
            return checked
              ? onChange([...value, option.value])
              : onChange(value.filter((value) => value !== option.value));
          }}
          disabled={disabled}
          {...rest}
        >
          <Checkbox.Label>
            <MarkdownContent>{option.label}</MarkdownContent>
          </Checkbox.Label>
        </Checkbox.Item>
      ))}
    </div>
  );
};
